import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable, combineLatest, Subscription } from "rxjs";
import { delay, map, take } from "rxjs/operators";
import * as L from "leaflet";
import "leaflet.markercluster";
import { Router } from "@angular/router";
import { RootStoreState } from "../../root-store";
import {
  networksSelector,
  stationsSelectors,
} from "../../root-store/station/selectors";

@Component({
  selector: "app-stations-map",
  templateUrl: "./stations-map.component.html",
  styleUrls: ["./stations-map.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationsMapComponent implements OnInit, AfterViewInit, OnDestroy {
  private map: L.Map;
  private resizeListener: () => void;
  private markerClusterGroup: L.MarkerClusterGroup;

  networks$: Observable<any>;
  stations$: Observable<any>;
  filteredStations$: Observable<any>;
  visibleStations$: Observable<any>;

  numberStations: number = 0;
  filtersVisible: boolean = false;
  bottomSheetOpen: boolean = false;

  selectedNetwork: any = null;
  selectedStation: any = null;
  userLocation: { lat: number; lng: number } | null = null;
  isGettingLocation: boolean = false;
  distanceFilter: number | null = null;

  private search$ = new BehaviorSubject<string>("");
  private onlyRequests$ = new BehaviorSubject<boolean>(false);
  private distanceFilter$ = new BehaviorSubject<number | null>(null);
  private userLocationMarker: L.Marker | null = null;

  constructor(
    private store: Store<RootStoreState.State>,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.networks$ = this.store.select(networksSelector);
    this.stations$ = this.store.select(stationsSelectors);

    // Aggiungi la funzione globale per il bottone nel popup
    (window as any).openIntervention = (
      stationId: string,
      stationName: string,
      networkId: string,
      networkName: string
    ) => {
      const station = {
        id_stazione: stationId,
        nome: stationName,
      };
      const network = {
        id: networkId,
        id_rete: networkId,
        nome: networkName,
        name: networkName,
      };
      this.openIntervention(station, network);
    };

    // Observable per le stazioni filtrate per rete
    this.filteredStations$ = this.stations$.pipe(
      map((stations) => {
        if (!stations || !this.selectedNetwork) {
          return stations || [];
        }
        return (stations || []).filter(
          (station) =>
            station.id_rete === this.selectedNetwork.id ||
            station.id_rete === this.selectedNetwork.id_rete
        );
      })
    );

    // Applica filtro testo, filtro 'solo con richieste' e filtro distanza sulle stazioni già filtrate per rete
    this.visibleStations$ = combineLatest([
      this.filteredStations$,
      this.search$,
      this.onlyRequests$,
      this.distanceFilter$,
    ]).pipe(
      map(([stations, term, onlyReq, distanceKm]) => {
        const q = (term || "").trim().toLowerCase();
        let result = stations || [];

        // Filtro per nome
        if (q) {
          result = result.filter((s) =>
            (s.nome || "").toString().toLowerCase().includes(q)
          );
        }

        // Filtro per richieste
        if (onlyReq) {
          result = result.filter(
            (s) => Array.isArray(s.requests) && s.requests.length > 0
          );
        }

        // Filtro per distanza (solo se abbiamo posizione utente e distanza specificata)
        if (distanceKm && this.userLocation) {
          result = result.filter((station) => {
            const distance = this.calculateDistanceToUser(station);
            return distance !== null && distance <= distanceKm;
          });
        }

        return result;
      })
    );

    // Se presente, seleziona di default la rete "MARCHE"
    this.networks$
      .pipe(
        map((list) =>
          (list || []).find(
            (n) => (n.id || "").toString().trim().toUpperCase() === "MARCHE"
          )
        ),
        take(1),
        delay(300)
      )
      .subscribe((defaultNetwork) => {
        if (defaultNetwork) {
          this.selectedNetwork = defaultNetwork;
          // Aggiorna marker e centra la mappa come per una selezione manuale
          this.clearMarkers();
          this.updateMarkers();
          this.centerMapOnFirstStation();
          // Forza aggiornamento vista con OnPush
          this.cdr.markForCheck();
        }
      });
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.clearMarkers();
    if (this.map) {
      this.map.remove();
    }
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
    }
    // Pulisci la funzione globale
    delete (window as any).openIntervention;
  }

  private initMap(): void {
    this.map = L.map("map").setView([41.9028, 12.4964], 10);

    // Aggiungi il layer di tile da OpenStreetMap
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
    }).addTo(this.map);

    // Inizializza il marker cluster group
    this.markerClusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 30,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      disableClusteringAtZoom: 12,
    });
    this.map.addLayer(this.markerClusterGroup);

    // Listener per il resize della finestra
    this.resizeListener = () => {
      if (this.map) {
        this.map.invalidateSize();
      }
    };
    window.addEventListener("resize", this.resizeListener);
  }

  onNetworkChange(network: any): void {
    this.selectedNetwork = network;

    this.clearMarkers();
    this.updateMarkers();
    this.centerMapOnFirstStation();
  }

  onSearchChange(term: string): void {
    this.search$.next(term || "");
    this.clearMarkers();
    this.updateMarkers();
    this.centerMapOnFirstStation();
  }

  onOnlyRequestsChange(checked: boolean): void {
    this.onlyRequests$.next(checked === true);
    this.clearMarkers();
    this.updateMarkers();
    this.centerMapOnFirstStation();
  }

  onDistanceFilterChange(value: string): void {
    const distance = value ? parseFloat(value) : null;
    this.distanceFilter = distance;

    // Debounce per evitare troppi aggiornamenti
    setTimeout(() => {
      this.distanceFilter$.next(distance);
      this.clearMarkers();
      this.updateMarkers();
      this.centerMapOnFirstStation();
    }, 500); // 500ms di delay
  }

  getUserLocation(): void {
    if (!navigator.geolocation) {
      alert("Geolocation non supportata da questo browser.");
      return;
    }

    this.isGettingLocation = true;
    this.cdr.markForCheck();

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.isGettingLocation = false;
        this.addUserLocationMarker();
        this.updateMarkers();
        this.cdr.markForCheck();
      },
      (error) => {
        this.isGettingLocation = false;
        this.cdr.markForCheck();
        console.error("Errore geolocation:", error);
        alert(
          "Impossibile ottenere la posizione. Controlla i permessi del browser."
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }

  private addUserLocationMarker(): void {
    if (!this.map || !this.userLocation) return;

    // Rimuovi marker precedente se esiste
    if (this.userLocationMarker) {
      this.map.removeLayer(this.userLocationMarker);
    }

    // Crea marker personalizzato per la posizione utente
    const userIcon = L.divIcon({
      html: `
        <div style="
          width: 20px;
          height: 20px;
          background-color: #1976d2;
          border: 3px solid #ffffff;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            width: 8px;
            height: 8px;
            background-color: #ffffff;
            border-radius: 50%;
          "></div>
        </div>
      `,
      className: "user-location-icon",
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    this.userLocationMarker = L.marker(
      [this.userLocation.lat, this.userLocation.lng],
      { icon: userIcon }
    ).addTo(this.map).bindPopup(`
        <div style="text-align: center; padding: 8px;">
          <h4 style="margin: 0 0 8px 0; color: #1976d2;">📍 La tua posizione</h4>
          <p style="margin: 0; font-size: 0.9em; color: #666;">
            Lat: ${this.userLocation.lat.toFixed(6)}<br>
            Lng: ${this.userLocation.lng.toFixed(6)}
          </p>
        </div>
      `);
  }

  centerOnUserLocation(): void {
    if (!this.map || !this.userLocation) return;
    this.map.setView([this.userLocation.lat, this.userLocation.lng], 15);
  }

  private calculateDistanceToUser(station: any): number | null {
    if (!this.userLocation) return null;

    const stationCoords = this.convertCoordinates(station);
    if (!stationCoords) return null;

    // Usa la formula di Haversine per calcolare la distanza
    const R = 6371; // Raggio della Terra in km
    const dLat = this.degreeToRadians(
      stationCoords.lat - this.userLocation.lat
    );
    const dLon = this.degreeToRadians(
      stationCoords.lng - this.userLocation.lng
    );
    const lat1 = this.degreeToRadians(this.userLocation.lat);
    const lat2 = this.degreeToRadians(stationCoords.lat);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  private degreeToRadians(deg: number): number {
    return deg * (Math.PI / 180);
  }

  toggleFiltersVisibility(): void {
    this.filtersVisible = !this.filtersVisible;
    this.cdr.markForCheck();
  }

  toggleBottomSheet(): void {
    this.bottomSheetOpen = !this.bottomSheetOpen;
    this.cdr.markForCheck();
  }

  closeBottomSheet(): void {
    this.bottomSheetOpen = false;
    this.cdr.markForCheck();
  }

  goBack(): void {
    this.router.navigate(['/app/dashboard']);
  }

  private clearMarkers(): void {
    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
    }

    // Rimuovi anche il marker della posizione utente se esiste
    if (this.userLocationMarker) {
      this.map.removeLayer(this.userLocationMarker);
      this.userLocationMarker = null;
    }
  }

  private updateMarkers(): void {
    if (!this.map) return;

    this.visibleStations$.pipe(take(1)).subscribe((stations) => {
      if (!stations || stations.length === 0) return;
      this.numberStations = stations.length;
      stations.forEach((station) => {
        const coordinates = this.convertCoordinates(station);
        if (coordinates) {
          // Crea un marker personalizzato con HTML
          const customIcon = L.divIcon({
            html: `
              <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 20px;
                max-width: 320px;
                width: fit-content;
                background: #fff;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.10);
                padding: 4px;
                gap: 8px;
                border: ${
                  station.requests && station.requests.length > 0
                    ? "2px solid #ff9800"
                    : ""
                };
                font-family: 'Roboto', Arial, sans-serif;
              ">
               
                <span style="
                  font-size: 1.05em;
                  font-weight: 600;
                  color: #222;
                  letter-spacing: 0.01em;
                  overflow-wrap: break-word;
                  text-align: center;
                  flex: 1 1 auto;
                ">
                  ${station.nome || "Stazione"}
                </span>
              </div>
            `,
            className: "custom-div-icon",
            iconSize: [120, 40],
            iconAnchor: [30, 40],
          });

          const marker = L.marker([coordinates.lat, coordinates.lng], {
            icon: customIcon,
          });

          (marker as any).bindPopup(() => {
            return this.generatePopupContent(station, coordinates);
          });

          this.markerClusterGroup.addLayers([marker]);
        }
      });
    });
  }

  private convertCoordinates(
    station: any
  ): { lat: number; lng: number } | null {
    // Controlla se esistono le coordinate in formato gradi/minuti/secondi
    if (
      station.lati1 !== undefined &&
      station.lati2 !== undefined &&
      station.lati3 !== undefined &&
      station.long1 !== undefined &&
      station.long2 !== undefined &&
      station.long3 !== undefined
    ) {
      // Converte da gradi/minuti/secondi a decimali
      const lat = this.dmsToDecimal(
        station.lati1,
        station.lati2,
        station.lati3
      );
      const lng = this.dmsToDecimal(
        station.long1,
        station.long2,
        station.long3
      );

      // Controlla che le coordinate siano valide
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }

    return null;
  }

  private dmsToDecimal(
    degrees: number,
    minutes: number,
    seconds: number
  ): number {
    // Converte gradi/minuti/secondi a decimali
    const sign = degrees < 0 ? -1 : 1;
    const absDegrees = Math.abs(degrees);
    return sign * (absDegrees + minutes / 60 + seconds / 3600);
  }

  private centerMapOnFirstStation(): void {
    if (!this.map) return;

    this.visibleStations$.pipe(take(1)).subscribe((stations) => {
      if (!stations || stations.length === 0) return;

      // Trova la prima stazione con coordinate valide
      for (const station of stations) {
        const coordinates = this.convertCoordinates(station);
        if (coordinates) {
          // Centra la mappa sulla prima stazione con zoom 12
          this.map.setView([coordinates.lat, coordinates.lng], 8);
          break; // Esce dal loop dopo aver trovato la prima stazione valida
        }
      }
    });
  }

  openIntervention(station: any, network: any): void {
    const stationData = {
      stationId: station.id_stazione,
      networkId: network.id || network.id_rete,
    };

    this.router.navigate(["/app/operation/new"], { queryParams: stationData });
  }

  private generatePopupContent(
    station: any,
    coordinates: { lat: number; lng: number }
  ): string {
          const distanceToUser = this.calculateDistanceToUser(station);
          const distanceText = distanceToUser
            ? `<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
              <i class="material-icons" style="font-size: 1.8rem; color: #899aa2;">straighten</i>
              <span style="font-size: 1rem;color: #5c676c;">
                <span>${distanceToUser.toFixed(2)} km</span>
              </span>
            </div>`
            : "";

          const networkId =
            (this.selectedNetwork &&
              (this.selectedNetwork.id || this.selectedNetwork.id_rete)) ||
            "";
          const networkName =
            (this.selectedNetwork &&
              (this.selectedNetwork.nome ||
                this.selectedNetwork.name ||
                this.selectedNetwork.id)) ||
            "";

    return `
              <div style="
                border-radius: 14px;
                padding: 8px;
                min-width: 260px;
                max-width: 340px;
                font-family: 'Roboto', Arial, sans-serif;
                color: #222;
                ">
                <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f5f5f5;">
                  <div style="
                    color: #1976d2;
                    font-weight: 700;
                    font-size: 1.2rem;
                    border-radius: 8px;
                    letter-spacing: 0.5px;
                  ">
                    ${station.id_stazione}
                  </div>
                  <span style="
                    font-size: 1.2rem;
                    font-weight: 500;
                    color: #222;
                    white-space: pre-line;
                    overflow-wrap: break-word;
                  ">${station.nome || "Stazione"}</span>
                </div>
                ${distanceText}
                ${
                  station.requests && station.requests.length > 0
                    ? `
          <div style="margin-bottom: 10px; border-radius: 8px;">
                    <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                      <i class="material-icons" style="font-size: 1.8rem; color: #ff9800;">assignment</i>
                      <strong style="color: #ff9800; font-size: 1rem;">Richieste</strong>
                    </div>
                    <ul style="padding-left: 18px; margin: 0;">
                      ${station.requests
                        .map(
                          (req) => `
                        <li style="font-size: 0.98em; color: #444; margin-bottom: 2px;">
                          ${
                            req.note
                              ? req.note
                              : '<span style="color:#aaa;">(Nessuna descrizione)</span>'
                          }
                        </li>
                      `
                        )
                        .join("")}
                    </ul>
                  </div>
                `
                    : ""
                }
                <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
                  <i class="material-icons" style="font-size: 1.8rem; color: #43a047;">place</i>
                    <span style="font-size: 1rem;color:#2e6c30">${coordinates.lat.toFixed(
                      6
                    )}, ${coordinates.lng.toFixed(6)}</span>
                </div>
                <div style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
                  <button onclick="window.openIntervention('${
                    station.id_stazione
                  }', '${station.nome}', '${networkId}', '${networkName}')" 
                          style="
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            background: #1976d2;
                            color: white;
                            border: none;
                            border-radius: 8px;
                            padding: 10px 16px;
                            font-size: 1rem;
                            font-weight: 500;
                            cursor: pointer;
                            transition: background-color 0.2s;
                            width: 100%;
                            justify-content: center;
                          "
                          onmouseover="this.style.backgroundColor='#1565c0'"
                          onmouseout="this.style.backgroundColor='#1976d2'"
                  >
                    <i class="material-icons" style="font-size: 1.2rem;">play_arrow</i>
                    Apri Intervento
                  </button>
                  <a href="https://www.google.com/maps?q=${coordinates.lat},${
            coordinates.lng
          }" 
                     target="_blank" 
                     style="
                       display: flex;
                       align-items: center;
                       gap: 4px;
              color: #1976d2;
                       text-decoration: none;
                       font-weight: 500;
                       border-radius: 6px;
                       font-size: 1rem;
                       justify-content: center;
                           border: 1px solid #1976d2;
    padding: 4px;
                     "
                  >
                    <i class="material-icons" style="color: #104477; font-size: 1.8rem;">map</i>
                    Apri in Google Maps
                  </a>
                </div>
              </div>
    `;
  }
}
